import React, { useEffect, useRef, useState } from "react";
import { PrimaryButton } from "../UI";
import { useUserContext } from "../../contexts/UserContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../UI/Alert/SuccessAlert";
import Label from "../UI/Label";
import Loader from "../UI/Loader/Loader";
import { Field, Form, Formik } from "formik";
import { tealiumDataLayerRepositorySchema } from "../../schemas";
import Dropdown from "../UI/Dropdown/Dropdown";
import { getSavedTasks, saveTask, updateTask, validateTealiumDataLayer, getUserJourneys, getUserJourneyById, createUserJourney, updateUserJourney, deleteUserJourney, linkJourneysToTask } from "../../utils/APIs";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import { toast } from "react-toastify";
import Tooltip from "../UI/Tooltip";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import Toggle from "../UI/Toggle";

// ─── Constants ────────────────────────────────────────────────────────────────

const RULE_TYPES = [
    { name: "Mandatory",     value: "mandatory"     },
    { name: "One of Values", value: "one_of_values" },
    { name: "Regex",         value: "regex"         },
];

const SCOPE_TYPES = [
    { name: "Global",     value: "global"     },
    { name: "Page Level", value: "page_level" },
];

const SCOPE_FILTER_OPTIONS = [
    { label: "All",        value: "all"        },
    { label: "Global",     value: "global"     },
    { label: "Page Level", value: "page_level" },
];

const EMPTY_RULE = {
    variable_name: "",
    scope: "global",
    rule_type: "mandatory",
    allowed_values: [],
    regex_pattern: "",
    description: "",
    page_scope: {
        urls: [],
        url_regex_patterns: [],
        where_variable_is_present: false,
    },
};

// Supported crawler step action types
const STEP_ACTIONS = [
    { name: "Go To URL",          value: "go_to"              },
    { name: "Click",              value: "click"              },
    { name: "Fill",               value: "fill"               },
    { name: "Wait for Selector",  value: "wait_for_selector"  },
    { name: "Wait for Enabled",   value: "wait_for_enabled"   },
    { name: "Accept Cookies",     value: "accept_cookies"     },
    { name: "Select Option",      value: "select_option"      },
    { name: "Press Key",          value: "press_key"          },
    { name: "Upload File",        value: "upload_file"        },
];

const SELECT_BY_OPTIONS = [
    { name: "By Value",  value: "value" },
    { name: "By Label",  value: "label" },
    { name: "By Index",  value: "index" },
    { name: "By Name",   value: "name"  },
];

const STEP_ACTION_META = {
    go_to:             { color: "bg-blue-100 text-blue-700",    icon: "→" },
    click:             { color: "bg-amber-100 text-amber-700",  icon: "↗" },
    fill:              { color: "bg-violet-100 text-violet-700",icon: "✎" },
    wait_for_selector: { color: "bg-gray-100 text-gray-500",    icon: "⏱" },
    wait_for_enabled:  { color: "bg-yellow-100 text-yellow-700",icon: "⊘" },
    accept_cookies:    { color: "bg-green-100 text-green-700",  icon: "✓" },
    select_option:     { color: "bg-teal-100 text-teal-700",    icon: "▾" },
    press_key:         { color: "bg-pink-100 text-pink-700",    icon: "⌨" },
    upload_file:       { color: "bg-indigo-100 text-indigo-700",icon: "↑" },
};

const emptyStep = (action = "go_to") => ({
    _key: Math.random().toString(36).slice(2),
    action,
    url: "",
    selector: "",
    value: "",
    force: false,
    key: "",
    selectBy: "value",
    selectValue: "",
    path: "",
    timeout: "",
    state: "visible",
    uploadMode: "path",
    base64: "",
    fileName: "",
    mimeType: "",
    cookieSelector: "",
});

// ─── RuleFormModal ────────────────────────────────────────────────────────────

const RuleFormModal = ({ initialRule, onSave, onClose }) => {
    const [rule, setRule] = useState(() => ({
        ...EMPTY_RULE,
        ...initialRule,
        page_scope: { ...EMPTY_RULE.page_scope, ...(initialRule?.page_scope ?? {}) },
    }));

    const [allowedValueInput, setAllowedValueInput] = useState("");
    const [urlInput,          setUrlInput]          = useState("");
    const [urlRegexInput,     setUrlRegexInput]     = useState("");
    const [errors,            setErrors]            = useState({});

    const update          = (patch) => setRule((r) => ({ ...r, ...patch }));
    const updatePageScope = (patch) =>
        setRule((r) => ({ ...r, page_scope: { ...r.page_scope, ...patch } }));

    const addToArray = (field, value, setter) => {
        const trimmed = value.trim();
        if (!trimmed) return;
        if (field === "allowed_values") {
            update({ allowed_values: [...rule.allowed_values, trimmed] });
        } else {
            updatePageScope({ [field]: [...(rule.page_scope?.[field] ?? []), trimmed] });
        }
        setter("");
    };

    const removeFromArray = (field, i) => {
        if (field === "allowed_values") {
            update({ allowed_values: rule.allowed_values.filter((_, idx) => idx !== i) });
        } else {
            updatePageScope({
                [field]: (rule.page_scope?.[field] ?? []).filter((_, idx) => idx !== i),
            });
        }
    };

    const validate = () => {
        const e = {};
        if (!rule.variable_name.trim()) e.variable_name = "Variable name is required";
        if (rule.rule_type === "one_of_values" && rule.allowed_values.length === 0)
            e.allowed_values = "Add at least one allowed value";
        if (rule.rule_type === "regex" && !rule.regex_pattern.trim())
            e.regex_pattern = "Regex pattern is required";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = () => { if (validate()) onSave(rule); };

    const TagInput = ({ tags, field, inputValue, setInputValue, placeholder, tagClass }) => (
        <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 w-full text-sm bg-white rounded-lg border border-gray-300 focus-within:ring-1 focus-within:ring-primary-2 min-h-[42px]">
            {tags.map((v, i) => (
                <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${tagClass}`}>
                    {v}
                    <button type="button" onClick={() => removeFromArray(field, i)} className="hover:text-red-5 ml-0.5">&times;</button>
                </span>
            ))}
            <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); addToArray(field, inputValue, setInputValue); }
                }}
                placeholder={placeholder}
                className="flex-1 min-w-[140px] outline-none bg-transparent border-none p-0 text-sm"
            />
        </div>
    );

    return (
        <PrimaryModal
            title={initialRule ? "Edit Rule" : "Add Validation Rule"}
            hideModal={onClose}
            widthClass="max-w-xl"
        >
            <div className="p-5 max-h-[80vh] overflow-y-auto space-y-4">

                {/* Variable Name */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                        Variable Name <span className="text-red-5">*</span>
                    </label>
                    <input
                        type="text"
                        value={rule.variable_name}
                        onChange={(e) => update({ variable_name: e.target.value })}
                        placeholder="e.g. page_type"
                        className="w-full px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-2"
                    />
                    {errors.variable_name && (
                        <p className="mt-1 text-xs text-red-5">{errors.variable_name}</p>
                    )}
                </div>

                {/* Rule Type + Scope */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Rule Type <span className="text-red-5">*</span>
                        </label>
                        <Dropdown
                            width={100}
                            isWidthInPercentage
                            items={RULE_TYPES}
                            initialValue={RULE_TYPES.find((r) => r.value === rule.rule_type)}
                            callback={(val) => update({ rule_type: val.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Scope <span className="text-red-5">*</span>
                        </label>
                        <Dropdown
                            width={100}
                            isWidthInPercentage
                            items={SCOPE_TYPES}
                            initialValue={SCOPE_TYPES.find((s) => s.value === rule.scope)}
                            callback={(val) => update({ scope: val.value })}
                        />
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                    <input
                        type="text"
                        value={rule.description}
                        onChange={(e) => update({ description: e.target.value })}
                        placeholder="Optional description"
                        className="w-full px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-2"
                    />
                </div>

                {/* One of Values */}
                {rule.rule_type === "one_of_values" && (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Allowed Values <span className="text-red-5">*</span>
                        </label>
                        <TagInput
                            tags={rule.allowed_values}
                            field="allowed_values"
                            inputValue={allowedValueInput}
                            setInputValue={setAllowedValueInput}
                            placeholder="Type a value and press Enter"
                            tagClass="bg-primary-2/10 text-primary-2"
                        />
                        <p className="text-xs text-gray-400 mt-1">Press Enter to add each value</p>
                        {errors.allowed_values && (
                            <p className="mt-1 text-xs text-red-5">{errors.allowed_values}</p>
                        )}
                    </div>
                )}

                {/* Regex */}
                {rule.rule_type === "regex" && (
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            Regex Pattern <span className="text-red-5">*</span>
                        </label>
                        <input
                            type="text"
                            value={rule.regex_pattern}
                            onChange={(e) => update({ regex_pattern: e.target.value })}
                            placeholder="e.g. ^[A-Z][a-z]+$"
                            className="w-full px-3 py-2 text-sm font-mono text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-2"
                        />
                        {errors.regex_pattern && (
                            <p className="mt-1 text-xs text-red-5">{errors.regex_pattern}</p>
                        )}
                    </div>
                )}

                {/* Page scope options */}
                {rule.scope === "page_level" && (
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-4 bg-gray-50">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Page Scope</p>

                        <div className="flex items-start gap-3">
                            <Toggle
                                id="page-scope-variable-present"
                                checked={rule.page_scope?.where_variable_is_present ?? false}
                                onChange={(e) => updatePageScope({ where_variable_is_present: e.target.checked })}
                                size="md"
                            />
                            <div>
                                <label htmlFor="page-scope-variable-present" className="text-sm font-medium text-gray-700 cursor-pointer">
                                    Where variable is present
                                </label>
                                <p className="text-xs text-gray-400">Applies to any page where this variable exists in the data layer</p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Exact Page URLs</label>
                            <TagInput
                                tags={rule.page_scope?.urls ?? []}
                                field="urls"
                                inputValue={urlInput}
                                setInputValue={setUrlInput}
                                placeholder="https://example.com/page"
                                tagClass="bg-blue-100 text-blue-700"
                            />
                            <p className="text-xs text-gray-400 mt-1">Press Enter to add each URL</p>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">URL Regex Patterns</label>
                            <TagInput
                                tags={rule.page_scope?.url_regex_patterns ?? []}
                                field="url_regex_patterns"
                                inputValue={urlRegexInput}
                                setInputValue={setUrlRegexInput}
                                placeholder="^https://example\.com/.*"
                                tagClass="bg-purple-100 text-purple-700 font-mono"
                            />
                            <p className="text-xs text-gray-400 mt-1">Press Enter to add each pattern</p>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
                    >
                        {initialRule ? "Save Changes" : "Add Rule"}
                    </button>
                </div>
            </div>
        </PrimaryModal>
    );
};

// ─── Badges ───────────────────────────────────────────────────────────────────

const RULE_TYPE_STYLES = {
    mandatory:     "bg-orange-100 text-orange-700",
    one_of_values: "bg-blue-100 text-blue-700",
    regex:         "bg-purple-100 text-purple-700",
};

const RuleTypeBadge = ({ type }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${RULE_TYPE_STYLES[type] ?? "bg-gray-100 text-gray-600"}`}>
        {type?.replace(/_/g, " ")}
    </span>
);

const ScopeBadge = ({ scope }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        scope === "global" ? "bg-green-100 text-green-700" : "bg-indigo-100 text-indigo-700"
    }`}>
        {scope === "global" ? "Global" : "Page Level"}
    </span>
);

// ─── ValidationRulesSection ───────────────────────────────────────────────────

const ValidationRulesSection = ({ values, setFieldValue }) => {
    const rules = values.validation_schedule?.rules ?? [];

    const [scopeFilter, setScopeFilter] = useState("all");
    const [ruleModal,   setRuleModal]   = useState(null);

    const updateSchedule = (patch) =>
        setFieldValue("validation_schedule", { ...(values.validation_schedule ?? {}), ...patch });

    const handleAddRule  = (rule) => { updateSchedule({ rules: [...rules, rule] }); setRuleModal(null); };
    const handleEditRule = (index, rule) => {
        const next = [...rules]; next[index] = rule;
        updateSchedule({ rules: next }); setRuleModal(null);
    };
    const handleRemoveRule = (index) =>
        updateSchedule({ rules: rules.filter((_, i) => i !== index) });

    const filteredRules = rules
        .map((r, originalIndex) => ({ ...r, originalIndex }))
        .filter((r) => scopeFilter === "all" || r.scope === scopeFilter);

    const ruleDetail = (rule) => {
        if (rule.rule_type === "one_of_values")
            return rule.allowed_values?.length
                ? rule.allowed_values.join(", ")
                : <span className="text-gray-400 italic">no values</span>;
        if (rule.rule_type === "regex")
            return rule.regex_pattern
                ? <code className="font-mono text-xs">{rule.regex_pattern}</code>
                : <span className="text-gray-400 italic">no pattern</span>;
        return <span className="text-gray-400 italic">—</span>;
    };

    return (
        <div className="mt-8">
            <div className="flex items-center gap-3 mb-1">
                <h3 className="text-base font-semibold text-black">Data Validation Rules</h3>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                    {rules.length} {rules.length === 1 ? "rule" : "rules"}
                </span>
                <div className="relative">
                    <Tooltip
                        content="Rules are evaluated against the Tealium data layer on each crawl"
                        class_name="-right-6 top-1/2"
                    />
                </div>
            </div>
            <p className="text-sm text-gray-500 mb-5">
                Define rules to validate variables in the captured data layer. Validation runs automatically after each crawl.
            </p>

            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                    {SCOPE_FILTER_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => setScopeFilter(opt.value)}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                scopeFilter === opt.value
                                    ? "bg-white text-gray-900 shadow-sm"
                                    : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {opt.label}
                            {opt.value !== "all" && (
                                <span className="ml-1.5 text-gray-400">
                                    {rules.filter((r) => r.scope === opt.value).length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={() => setRuleModal({ mode: "add" })}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 transition-opacity"
                >
                    <span className="text-base leading-none">+</span>
                    Add Rule
                </button>
            </div>

            {filteredRules.length === 0 ? (
                <div className="border-2 border-dashed border-gray-200 rounded-lg py-10 text-center">
                    <p className="text-sm text-gray-400">
                        {rules.length === 0
                            ? "No validation rules yet."
                            : `No ${scopeFilter === "page_level" ? "page level" : scopeFilter} rules.`}
                    </p>
                    {rules.length === 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                            Click "Add Rule" to define your first data layer variable rule.
                        </p>
                    )}
                </div>
            ) : (
                <div className="rounded-lg border border-gray-200 overflow-x-auto">
                    <table className="min-w-full text-sm text-left whitespace-nowrap">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Variable</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Scope</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Detail</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Description</th>
                                <th className="px-4 py-3 w-24"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredRules.map((rule) => (
                                <tr key={rule.originalIndex} className="bg-white hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <code className="text-xs font-mono font-semibold text-gray-800">
                                            {rule.variable_name || <span className="text-gray-400 italic font-sans font-normal">unnamed</span>}
                                        </code>
                                    </td>
                                    <td className="px-4 py-3"><RuleTypeBadge type={rule.rule_type} /></td>
                                    <td className="px-4 py-3"><ScopeBadge scope={rule.scope} /></td>
                                    <td className="px-4 py-3 text-xs text-gray-600">{ruleDetail(rule)}</td>
                                    <td className="px-4 py-3 text-xs text-gray-500">
                                        {rule.description || <span className="italic text-gray-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3 justify-end">
                                            <button type="button" onClick={() => setRuleModal({ mode: "edit", index: rule.originalIndex })}
                                                className="text-primary hover:opacity-70 text-xs font-medium">Edit</button>
                                            <button type="button" onClick={() => handleRemoveRule(rule.originalIndex)}
                                                className="text-red-5 hover:opacity-70 text-xs font-medium">Remove</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {ruleModal && (
                <RuleFormModal
                    initialRule={ruleModal.mode === "edit" ? rules[ruleModal.index] : null}
                    onSave={(rule) =>
                        ruleModal.mode === "edit"
                            ? handleEditRule(ruleModal.index, rule)
                            : handleAddRule(rule)
                    }
                    onClose={() => setRuleModal(null)}
                />
            )}
        </div>
    );
};

// ─── StepCard ─────────────────────────────────────────────────────────────────

const StepCard = ({ step, index, total, onChange, onRemove, onMoveUp, onMoveDown }) => {
    const meta = STEP_ACTION_META[step.action] ?? { fields: [], color: "bg-gray-100 text-gray-600", icon: "•" };
    const isFirst = index === 0;

    const inputClass = "w-full px-3 py-2 text-sm text-gray-900 bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-2 placeholder:text-gray-400";

    return (
        <div className="relative group">
            {/* Connector line between steps */}
            {index < total - 1 && (
                <div className="absolute left-[22px] top-full w-px h-3 bg-gray-300 z-10" />
            )}

            <div className="flex items-start gap-3">
                {/* Step number + icon */}
                <div className="flex-shrink-0 flex flex-col items-center">
                    <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center text-xs font-bold shadow-sm border border-white ${meta.color}`}>
                        <span className="text-base leading-none">{meta.icon}</span>
                        <span className="text-[10px] opacity-70 mt-0.5">{index + 1}</span>
                    </div>
                </div>

                {/* Card body */}
                <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:border-gray-300 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                        {/* Action selector */}
                        <div className="w-48">
                            <Dropdown
                                width={100}
                                isWidthInPercentage
                                items={isFirst
                                    ? [{ name: "Go To URL", value: "go_to" }]
                                    : STEP_ACTIONS.filter(a => a.value !== "go_to")
                                }
                                initialValue={STEP_ACTIONS.find(a => a.value === step.action)}
                                callback={(val) => onChange({ ...emptyStep(val.value), _key: step._key })}
                            />
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button type="button" onClick={onMoveUp} disabled={index === 0 || isFirst}
                                className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors text-xs">
                                ↑
                            </button>
                            <button type="button" onClick={onMoveDown} disabled={index === total - 1}
                                className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-20 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-colors text-xs">
                                ↓
                            </button>
                            {!isFirst && (
                                <button type="button" onClick={onRemove}
                                    className="p-1.5 text-gray-400 hover:text-red-5 rounded-lg hover:bg-red-50 transition-colors text-xs ml-1">
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Fields */}
                    <div className="space-y-2">
                        {/* go_to */}
                        {step.action === "go_to" && (
                            <input type="text" value={step.url} onChange={(e) => onChange({ ...step, url: e.target.value })}
                                placeholder="https://example.com/page" className={inputClass} />
                        )}

                        {/* Selector field — shared by several actions */}
                        {["click", "fill", "wait_for_selector", "wait_for_enabled", "select_option", "upload_file"].includes(step.action) && (
                            <input type="text" value={step.selector} onChange={(e) => onChange({ ...step, selector: e.target.value })}
                                placeholder="CSS selector — e.g. button.submit, #login-btn" className={`${inputClass} font-mono text-xs`} />
                        )}

                        {/* wait_for_selector — state + timeout */}
                        {step.action === "wait_for_selector" && (<>
                            <div className="flex gap-2">
                                {["visible","hidden","attached"].map(s => (
                                    <button key={s} type="button"
                                        onClick={() => onChange({ ...step, state: s })}
                                        className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${step.state === s ? "bg-primary text-white border-primary" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <input type="number" value={step.timeout ?? ""} onChange={(e) => onChange({ ...step, timeout: e.target.value })}
                                placeholder="Timeout ms (default 30000)" className={inputClass} min="0" />
                            <p className="text-xs text-gray-400 italic">Waits until this selector reaches the chosen state before continuing</p>
                        </>)}

                        {/* wait_for_enabled — timeout */}
                        {step.action === "wait_for_enabled" && (<>
                            <input type="number" value={step.timeout ?? ""} onChange={(e) => onChange({ ...step, timeout: e.target.value })}
                                placeholder="Timeout ms (default 30000)" className={inputClass} min="0" />
                            <p className="text-xs text-gray-400 italic">Waits until this element is no longer disabled</p>
                        </>)}

                        {/* fill value */}
                        {step.action === "fill" && (
                            <input type="text" value={step.value} onChange={(e) => onChange({ ...step, value: e.target.value })}
                                placeholder="Value to fill in" className={inputClass} />
                        )}

                        {/* click force */}
                        {step.action === "click" && (
                            <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
                                <input type="checkbox" checked={step.force ?? false}
                                    onChange={(e) => onChange({ ...step, force: e.target.checked })}
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer" />
                                Force click (bypasses visibility checks)
                            </label>
                        )}

                        {/* accept_cookies — optional selector + timeout */}
                        {step.action === "accept_cookies" && (<>
                            <input type="text" value={step.cookieSelector ?? ""} onChange={(e) => onChange({ ...step, cookieSelector: e.target.value })}
                                placeholder="Custom selector — leave blank for auto-detect" className={`${inputClass} font-mono text-xs`} />
                            <input type="number" value={step.timeout ?? ""} onChange={(e) => onChange({ ...step, timeout: e.target.value })}
                                placeholder="Timeout ms (default 5000)" className={inputClass} min="0" />
                            <p className="text-xs text-gray-400 italic">Automatically finds and clicks a cookie consent button</p>
                        </>)}

                        {/* select_option — select-by toggle + value */}
                        {step.action === "select_option" && (
                            <div className="flex gap-2">
                                <div className="w-36 flex-shrink-0">
                                    <Dropdown
                                        width={100}
                                        isWidthInPercentage
                                        items={SELECT_BY_OPTIONS}
                                        initialValue={SELECT_BY_OPTIONS.find(o => o.value === (step.selectBy ?? "value"))}
                                        callback={(val) => onChange({ ...step, selectBy: val.value })}
                                    />
                                </div>
                                <input type="text" value={step.selectValue ?? ""} onChange={(e) => onChange({ ...step, selectValue: e.target.value })}
                                    placeholder={step.selectBy === "index" ? "0" : "Option value / label"}
                                    className={inputClass} />
                            </div>
                        )}

                        {/* press_key */}
                        {step.action === "press_key" && (
                            <input type="text" value={step.key ?? ""} onChange={(e) => onChange({ ...step, key: e.target.value })}
                                placeholder="e.g. Enter, Tab, ArrowDown, Escape" className={inputClass} />
                        )}

                        {/* upload_file — path or base64 */}
                        {step.action === "upload_file" && (<>
                            <div className="flex gap-2">
                                {["path","base64"].map(m => (
                                    <button key={m} type="button"
                                        onClick={() => onChange({ ...step, uploadMode: m })}
                                        className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${(step.uploadMode ?? "path") === m ? "bg-primary text-white border-primary" : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}>
                                        {m === "path" ? "Path" : "Base64"}
                                    </button>
                                ))}
                            </div>
                            {(step.uploadMode ?? "path") === "path" ? (
                                <input type="text" value={step.path ?? ""} onChange={(e) => onChange({ ...step, path: e.target.value })}
                                    placeholder="Absolute file path — e.g. /tmp/document.pdf" className={inputClass} />
                            ) : (<>
                                <textarea value={step.base64 ?? ""} onChange={(e) => onChange({ ...step, base64: e.target.value })}
                                    placeholder="Base64-encoded file content" rows={3}
                                    className={`${inputClass} font-mono text-xs resize-y`} />
                                <input type="text" value={step.fileName ?? ""} onChange={(e) => onChange({ ...step, fileName: e.target.value })}
                                    placeholder="Filename — e.g. document.pdf" className={inputClass} />
                                <input type="text" value={step.mimeType ?? ""} onChange={(e) => onChange({ ...step, mimeType: e.target.value })}
                                    placeholder="MIME type — e.g. application/pdf" className={inputClass} />
                            </>)}
                        </>)}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Normalises a raw steps array from the Chromium recording extension into the
 * canonical instruction format the app stores.
 *
 * Handles:
 *  - `action: "upload"` → `action: "upload_file"` (strips data-URL prefix,
 *    renames fields: file→base64, filename→name, derives mime_type)
 *  - Strips recorder metadata: timestamp, _enabled, and the non-go_to `url`
 *    field the extension adds to every step
 */
const normalizeExtensionInstructions = (raw) =>
    raw.map((s) => {
        const { timestamp, _enabled, ...step } = s;

        if (step.action === "upload") {
            const dataUrl  = step.file ?? "";
            const base64   = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
            const mime_type = dataUrl.startsWith("data:") ? dataUrl.split(";")[0].slice(5) : "application/octet-stream";
            return { action: "upload_file", selector: step.selector, base64, name: step.filename ?? "", mime_type };
        }

        if (step.action !== "go_to") {
            const { url, ...rest } = step;
            return rest;
        }

        return step;
    });

/**
 * Converts the local path state (with _key UI helpers) into the clean
 * instruction array expected by the backend.
 */
const buildInstructions = (steps) =>
    steps.map(({ _key, selectBy, selectValue, path: filePath, timeout, state, uploadMode, base64, fileName, mimeType, cookieSelector, ...step }) => {
        const clean = { ...step };

        // Remove fields that don't apply to this action
        if (clean.action !== "go_to")         delete clean.url;
        if (clean.action !== "fill")          delete clean.value;
        if (clean.action !== "click")         delete clean.force;
        if (clean.action !== "press_key")     delete clean.key;
        if (!["click","fill","wait_for_selector","wait_for_enabled","select_option","upload_file"].includes(clean.action))
            delete clean.selector;

        // select_option: map selectBy/selectValue → the field the backend expects
        if (clean.action === "select_option") {
            const by  = selectBy    ?? "value";
            const val = selectValue ?? "";
            if (by === "index") clean.index = parseInt(val, 10) || 0;
            else                clean[by]   = val;
        }

        // upload_file: path or base64
        if (clean.action === "upload_file") {
            if ((uploadMode ?? "path") === "base64") {
                if (base64)    clean.base64    = base64;
                if (fileName)  clean.name      = fileName;
                if (mimeType)  clean.mime_type = mimeType;
            } else if (filePath) {
                clean.path = filePath;
            }
        }

        // wait_for_selector: state + optional timeout
        if (clean.action === "wait_for_selector") {
            clean.state = state ?? "visible";
            if (timeout !== "" && timeout != null) clean.timeout = parseInt(timeout, 10);
        }

        // wait_for_enabled: optional timeout
        if (clean.action === "wait_for_enabled") {
            if (timeout !== "" && timeout != null) clean.timeout = parseInt(timeout, 10);
        }

        // accept_cookies: optional custom selector + timeout
        if (clean.action === "accept_cookies") {
            if (cookieSelector) clean.selector = cookieSelector;
            if (timeout !== "" && timeout != null) clean.timeout = parseInt(timeout, 10);
        }

        return clean;
    });

/**
 * Converts saved path instructions back into the UI step format.
 */
const parseInstructionsToSteps = (instructions = []) =>
    instructions.map((s) => {
        const step = { ...s, _key: Math.random().toString(36).slice(2) };

        // Hydrate select_option UI fields from stored instruction
        if (step.action === "select_option") {
            if ("index" in step)       { step.selectBy = "index"; step.selectValue = String(step.index); }
            else if ("label" in step)  { step.selectBy = "label"; step.selectValue = step.label; }
            else if ("name" in step)   { step.selectBy = "name";  step.selectValue = step.name; }
            else                       { step.selectBy = "value"; step.selectValue = step.value ?? ""; }
        }

        // Hydrate upload_file fields
        if (step.action === "upload_file") {
            if ("base64" in step) {
                step.uploadMode = "base64";
                step.base64     = step.base64 ?? "";
                step.fileName   = step.name      ?? "";
                step.mimeType   = step.mime_type ?? "";
            } else {
                step.uploadMode = "path";
                step.path       = step.path ?? "";
            }
        }

        // Hydrate wait_for_selector state + timeout
        if (step.action === "wait_for_selector") {
            step.state   = step.state   ?? "visible";
            step.timeout = step.timeout != null ? String(step.timeout) : "";
        }

        // Hydrate wait_for_enabled timeout
        if (step.action === "wait_for_enabled") {
            step.timeout = step.timeout != null ? String(step.timeout) : "";
        }

        // Hydrate accept_cookies cookieSelector + timeout
        if (step.action === "accept_cookies") {
            step.cookieSelector = typeof step.selector === "string" ? step.selector : "";
            step.timeout        = step.timeout != null ? String(step.timeout) : "";
            delete step.selector;
        }

        // Ensure all UI-only fields exist
        step.selectBy      ??= "value";
        step.selectValue   ??= "";
        step.path          ??= "";
        step.timeout       ??= "";
        step.state         ??= "visible";
        step.uploadMode    ??= "path";
        step.base64        ??= "";
        step.fileName      ??= "";
        step.mimeType      ??= "";
        step.cookieSelector ??= "";
        return step;
    });

// ─── JourneysSection ─────────────────────────────────────────────────────────

const JourneysSection = ({
    orgJourneys, setOrgJourneys,
    selectedJourneyIds, setSelectedJourneyIds,
    journeyEdits, setJourneyEdits,
    journeySaving, setJourneySaving,
    creatingJourney, setCreatingJourney,
    token, organizationId,
}) => {
    const [newJourney,   setNewJourney]   = useState(null);
    const [uploadError,  setUploadError]  = useState("");
    const [uploading,    setUploading]    = useState(false);
    const fileInputRef = useRef(null);

    const handleJsonUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";
        setUploadError("");

        let parsed;
        try {
            let raw = JSON.parse(await file.text());
            if (!Array.isArray(raw)) raw = [raw];
            // Accept "steps" or "instructions" as the step array key.
            // The chrome extension export produces { steps: [...] } without a name —
            // fall back to the filename (without .json) in that case.
            const baseName = file.name.replace(/\.json$/i, "");
            raw = raw.map((p) => typeof p.name === "string" ? p : { ...p, name: baseName });
            const valid = raw.every((p) => p && typeof p.name === "string" && (Array.isArray(p.instructions) || Array.isArray(p.steps)));
            if (!valid) { setUploadError('Each journey must have a "name" (string) and "instructions" or "steps" (array).'); return; }
            parsed = raw;
        } catch {
            setUploadError("Invalid JSON file. Please check the format and try again.");
            return;
        }

        setUploading(true);
        try {
            const created = await Promise.all(
                parsed.map((p) => {
                    const rawSteps = p.instructions ?? p.steps;
                    const steps = normalizeExtensionInstructions(rawSteps);
                    return createUserJourney(token, organizationId, {
                        name: p.name.trim(),
                        steps,
                        min_step_delay: p.min_step_delay ?? 0,
                    }).then((res) => res.data);
                })
            );
            setOrgJourneys((prev) => [...prev, ...created]);
            setSelectedJourneyIds((prev) => new Set([...prev, ...created.map((j) => j._id.toString())]));
            toast.success(`${created.length} journey${created.length !== 1 ? "s" : ""} imported.`);
        } catch (err) {
            toast.error(err?.message || "Failed to import journeys.");
        } finally {
            setUploading(false);
        }
    };

    const toggleSelected = (id) => {
        setSelectedJourneyIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const startEdit = async (journey) => {
        try {
            const res = await getUserJourneyById(token, organizationId, journey._id);
            const full = res.data;
            setJourneyEdits((prev) => ({
                ...prev,
                [journey._id]: {
                    name: full.name,
                    min_step_delay: full.min_step_delay ?? 0,
                    steps: parseInstructionsToSteps(full.instructions ?? full.steps ?? []),
                    _key: Math.random().toString(36).slice(2),
                },
            }));
        } catch (err) {
            toast.error(err?.message || "Failed to load journey steps.");
        }
    };

    const cancelEdit = (id) => {
        setJourneyEdits((prev) => { const next = { ...prev }; delete next[id]; return next; });
    };

    const saveJourneyEdit = async (journey) => {
        const edit = journeyEdits[journey._id];
        if (!edit) return;
        if (!edit.name?.trim()) { toast.error("Journey name is required."); return; }
        const firstStep = edit.steps[0];
        if (!firstStep?.url?.trim()) { toast.error(`Journey "${edit.name}" must have a URL in the first step.`); return; }
        setJourneySaving((prev) => ({ ...prev, [journey._id]: true }));
        try {
            const res = await updateUserJourney(token, organizationId, journey._id, {
                name: edit.name.trim(),
                steps: buildInstructions(edit.steps),
                min_step_delay: edit.min_step_delay ?? 0,
            });
            setOrgJourneys((prev) => prev.map((j) => j._id === journey._id ? res.data : j));
            cancelEdit(journey._id);
            toast.success("Journey saved.");
        } catch (err) {
            toast.error(err?.message || "Failed to save journey.");
        } finally {
            setJourneySaving((prev) => { const next = { ...prev }; delete next[journey._id]; return next; });
        }
    };

    const handleDelete = async (journey) => {
        if (!window.confirm(`Delete journey "${journey.name}"? This will also unlink it from all tasks.`)) return;
        try {
            await deleteUserJourney(token, organizationId, journey._id);
            setOrgJourneys((prev) => prev.filter((j) => j._id !== journey._id));
            setSelectedJourneyIds((prev) => { const next = new Set(prev); next.delete(journey._id.toString()); return next; });
            cancelEdit(journey._id);
            toast.success("Journey deleted.");
        } catch (err) {
            toast.error(err?.message || "Failed to delete journey.");
        }
    };

    const saveNewJourney = async () => {
        if (!newJourney) return;
        if (!newJourney.name?.trim()) { toast.error("Journey name is required."); return; }
        const firstStep = newJourney.steps[0];
        if (!firstStep?.url?.trim()) { toast.error("Journey must have a URL in the first step."); return; }
        setCreatingJourney(true);
        try {
            const res = await createUserJourney(token, organizationId, {
                name: newJourney.name.trim(),
                steps: buildInstructions(newJourney.steps),
                min_step_delay: newJourney.min_step_delay ?? 0,
            });
            const created = res.data;
            setOrgJourneys((prev) => [...prev, created]);
            setSelectedJourneyIds((prev) => new Set([...prev, created._id.toString()]));
            setNewJourney(null);
            toast.success("Journey created.");
        } catch (err) {
            toast.error(err?.message || "Failed to create journey.");
        } finally {
            setCreatingJourney(false);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-black">E2E Journeys</h3>
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {orgJourneys.length} {orgJourneys.length === 1 ? "journey" : "journeys"} in org
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json,application/json"
                        className="hidden"
                        onChange={handleJsonUpload}
                    />
                    <button
                        type="button"
                        onClick={() => {
                            const schema = [{ name: "My Journey", min_step_delay: 0, steps: [{ action: "go_to", url: "https://example.com" }] }];
                            const blob = new Blob([JSON.stringify(schema, null, 2)], { type: "application/json" });
                            const a = document.createElement("a");
                            a.href = URL.createObjectURL(blob);
                            a.download = "journey-schema.json";
                            a.click();
                            URL.revokeObjectURL(a.href);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 border border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50 transition-all"
                    >
                        <span className="text-base leading-none">↓</span>
                        Download Schema
                    </button>
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 border border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50 transition-all disabled:opacity-50"
                    >
                        <span className="text-base leading-none">↑</span>
                        {uploading ? "Importing…" : "Upload JSON"}
                    </button>
                    {!newJourney && (
                        <button
                            type="button"
                            onClick={() => setNewJourney({ name: "", steps: [emptyStep("go_to")], min_step_delay: 0, _key: Math.random().toString(36).slice(2) })}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
                        >
                            <span className="text-base leading-none">+</span>
                            New Journey
                        </button>
                    )}
                </div>
            </div>
            <p className="text-sm text-gray-500 mb-5">
                Select which org-level journeys to run for this task. Use the toggle on each card to include or exclude a journey.
            </p>
            {uploadError && <p className="mb-4 text-xs text-red-500">{uploadError}</p>}

            {/* New journey card */}
            {newJourney && (
                <div className="mb-4 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
                        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">+</span>
                        </div>
                        <input
                            type="text"
                            value={newJourney.name}
                            onChange={(e) => setNewJourney((j) => ({ ...j, name: e.target.value }))}
                            placeholder="Journey name — e.g. Checkout Flow"
                            className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-none outline-none placeholder:text-gray-400 placeholder:font-normal"
                        />
                        <button type="button" onClick={() => setNewJourney(null)} className="p-1.5 text-gray-400 hover:text-red-5 rounded-lg hover:bg-red-50 text-xs">✕</button>
                    </div>
                    <div className="p-4 space-y-2">
                        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200">
                            <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Min time between steps (ms)</label>
                            <input
                                type="number" min="0" step="100"
                                value={newJourney.min_step_delay ?? 0}
                                onChange={(e) => setNewJourney((j) => ({ ...j, min_step_delay: Math.max(0, Number(e.target.value) || 0) }))}
                                className="w-28 text-sm border border-gray-200 rounded-lg px-2 py-1 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
                            />
                            <span className="text-xs text-gray-400">0 = no forced delay</span>
                        </div>
                        {newJourney.steps.map((step, stepIndex) => (
                            <StepCard
                                key={step._key ?? stepIndex}
                                step={step}
                                index={stepIndex}
                                total={newJourney.steps.length}
                                onChange={(updated) => setNewJourney((j) => { const s = [...j.steps]; s[stepIndex] = updated; return { ...j, steps: s }; })}
                                onRemove={() => { if (newJourney.steps.length > 1) setNewJourney((j) => ({ ...j, steps: j.steps.filter((_, i) => i !== stepIndex) })); }}
                                onMoveUp={() => {
                                    if (stepIndex <= 1) return;
                                    setNewJourney((j) => { const s = [...j.steps]; const [m] = s.splice(stepIndex, 1); s.splice(stepIndex - 1, 0, m); return { ...j, steps: s }; });
                                }}
                                onMoveDown={() => {
                                    if (stepIndex === newJourney.steps.length - 1 || stepIndex === 0) return;
                                    setNewJourney((j) => { const s = [...j.steps]; const [m] = s.splice(stepIndex, 1); s.splice(stepIndex + 1, 0, m); return { ...j, steps: s }; });
                                }}
                            />
                        ))}
                        <button type="button"
                            onClick={() => setNewJourney((j) => ({ ...j, steps: [...j.steps, emptyStep("click")] }))}
                            className="mt-3 ml-14 flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-800 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-white transition-all"
                        >
                            <span className="text-sm leading-none text-gray-400">+</span>
                            Add step
                        </button>
                    </div>
                    <div className="flex justify-end gap-2 px-4 pb-4">
                        <button type="button" onClick={() => setNewJourney(null)}
                            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="button" onClick={saveNewJourney} disabled={creatingJourney}
                            className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50">
                            {creatingJourney ? "Creating…" : "Create Journey"}
                        </button>
                    </div>
                </div>
            )}

            {/* Org journey list */}
            {orgJourneys.length === 0 && !newJourney ? (
                <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center">
                    <p className="text-sm text-gray-400">No journeys in this organisation yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Click "New Journey" to record your first E2E journey.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {orgJourneys.map((journey) => {
                        const id   = journey._id?.toString();
                        const edit = journeyEdits[journey._id];
                        const linked = selectedJourneyIds.has(id);
                        const saving = journeySaving[journey._id];

                        return (
                            <div key={journey._id} className={`rounded-xl border overflow-hidden ${linked ? "border-primary/40 bg-primary/5" : "border-gray-200 bg-white"}`}>
                                {/* Journey header */}
                                <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
                                    {/* Link toggle */}
                                    <Toggle
                                        id={`journey-link-${id}`}
                                        checked={linked}
                                        onChange={() => toggleSelected(id)}
                                        title={linked ? "Remove from this task" : "Add to this task"}
                                        size="md"
                                    />

                                    {edit ? (
                                        <input
                                            type="text"
                                            value={edit.name}
                                            onChange={(e) => setJourneyEdits((prev) => ({ ...prev, [journey._id]: { ...edit, name: e.target.value } }))}
                                            className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-none outline-none"
                                        />
                                    ) : (
                                        <span className="flex-1 text-sm font-medium text-gray-900 truncate">{journey.name}</span>
                                    )}

                                    <span className="text-xs text-gray-400 flex-shrink-0">
                                        {(journey.instructions?.length ?? 0)} step{journey.instructions?.length !== 1 ? "s" : ""}
                                    </span>

                                    {!edit ? (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button type="button" onClick={() => startEdit(journey)}
                                                className="p-1.5 text-gray-400 hover:text-primary rounded-lg hover:bg-primary/10 transition-colors text-xs">
                                                Edit
                                            </button>
                                            <button type="button" onClick={() => handleDelete(journey)}
                                                className="p-1.5 text-gray-400 hover:text-red-5 rounded-lg hover:bg-red-50 transition-colors text-xs">
                                                Delete
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button type="button" onClick={() => saveJourneyEdit(journey)} disabled={saving}
                                                className="px-3 py-1 text-xs font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50">
                                                {saving ? "Saving…" : "Save"}
                                            </button>
                                            <button type="button" onClick={() => cancelEdit(journey._id)}
                                                className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Step editor (only when editing) */}
                                {edit && (
                                    <div className="p-4 space-y-2">
                                        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-200">
                                            <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Min time between steps (ms)</label>
                                            <input
                                                type="number" min="0" step="100"
                                                value={edit.min_step_delay ?? 0}
                                                onChange={(e) => setJourneyEdits((prev) => ({ ...prev, [journey._id]: { ...edit, min_step_delay: Math.max(0, Number(e.target.value) || 0) } }))}
                                                className="w-28 text-sm border border-gray-200 rounded-lg px-2 py-1 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                            />
                                            <span className="text-xs text-gray-400">0 = no forced delay</span>
                                        </div>
                                        {edit.steps.map((step, stepIndex) => (
                                            <StepCard
                                                key={step._key ?? stepIndex}
                                                step={step}
                                                index={stepIndex}
                                                total={edit.steps.length}
                                                onChange={(updated) => {
                                                    const steps = [...edit.steps]; steps[stepIndex] = updated;
                                                    setJourneyEdits((prev) => ({ ...prev, [journey._id]: { ...edit, steps } }));
                                                }}
                                                onRemove={() => {
                                                    if (edit.steps.length > 1) {
                                                        const steps = edit.steps.filter((_, i) => i !== stepIndex);
                                                        setJourneyEdits((prev) => ({ ...prev, [journey._id]: { ...edit, steps } }));
                                                    }
                                                }}
                                                onMoveUp={() => {
                                                    if (stepIndex <= 1) return;
                                                    const steps = [...edit.steps]; const [m] = steps.splice(stepIndex, 1); steps.splice(stepIndex - 1, 0, m);
                                                    setJourneyEdits((prev) => ({ ...prev, [journey._id]: { ...edit, steps } }));
                                                }}
                                                onMoveDown={() => {
                                                    if (stepIndex === edit.steps.length - 1 || stepIndex === 0) return;
                                                    const steps = [...edit.steps]; const [m] = steps.splice(stepIndex, 1); steps.splice(stepIndex + 1, 0, m);
                                                    setJourneyEdits((prev) => ({ ...prev, [journey._id]: { ...edit, steps } }));
                                                }}
                                            />
                                        ))}
                                        <button type="button"
                                            onClick={() => {
                                                const steps = [...edit.steps, emptyStep("click")];
                                                setJourneyEdits((prev) => ({ ...prev, [journey._id]: { ...edit, steps } }));
                                            }}
                                            className="mt-3 ml-14 flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-800 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 hover:bg-white transition-all"
                                        >
                                            <span className="text-sm leading-none text-gray-400">+</span>
                                            Add step
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TealiumDataLayerRepository = () => {
    const { userData }      = useUserContext();
    const { pathname }      = useLocation();
    const { saved_task_id } = useParams();
    const navigate          = useNavigate();
    const { content }       = useContentfulContext();

    const [showSuccess] = useState("");
    const [showError,        setShowError]        = useState("");
    const [loading,          setLoading]          = useState(false);
    const [data,             setData]             = useState();
    const [visibilityModal,  setVisibilityModal]  = useState(false);
    const [visibilityParams, setVisibilityParams] = useState([]);
    const [activeTab,        setActiveTab]        = useState("path_crawls");

    // Org-level journeys and which ones are linked to this task
    const [orgJourneys,      setOrgJourneys]      = useState([]);
    const [selectedJourneyIds, setSelectedJourneyIds] = useState(new Set());
    // Track per-journey dirty edits: Map<_id, { name, steps, min_step_delay }>
    const [journeyEdits,     setJourneyEdits]     = useState({});
    const [journeySaving,    setJourneySaving]    = useState({});
    const [creatingJourney,  setCreatingJourney]  = useState(false);

    // Variable blacklist / whitelist for path crawl compare
    const [varBlacklist,  setVarBlacklist]  = useState([]);
    const [varWhitelist,  setVarWhitelist]  = useState([]);
    const [blackInput,    setBlackInput]    = useState("");
    const [whiteInput,    setWhiteInput]    = useState("");

    // Path crawl email notification settings
    const [pathEmailRecipients,      setPathEmailRecipients]      = useState([]);
    const [pathEmailInput,           setPathEmailInput]           = useState("");
    const [notifyOnError,            setNotifyOnError]            = useState(false);
    const [notifyOnWhitelistChange,  setNotifyOnWhitelistChange]  = useState(false);
    const [notifyOnSuccessNoChanges, setNotifyOnSuccessNoChanges] = useState(false);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [journeysRes, taskRes] = await Promise.all([
                getUserJourneys(userData?.token, userData?.selectedOrganization?.org_token),
                saved_task_id
                    ? getSavedTasks(userData?.token, userData?.selectedOrganization?.org_token, saved_task_id)
                    : Promise.resolve(null),
            ]);

            const journeys = Array.isArray(journeysRes?.data) ? journeysRes.data : [];
            setOrgJourneys(journeys);

            if (taskRes) {
                const task = taskRes.data[0];
                setData(task);
                setSelectedJourneyIds(new Set((task?.journey_ids ?? []).map(String)));
                setVarBlacklist(task?.variable_name_blacklist ?? []);
                setVarWhitelist(task?.variable_name_whitelist ?? []);
                const emailSettings = task?.path_crawl_email_settings;
                setPathEmailRecipients(emailSettings?.recipient_emails             ?? []);
                setNotifyOnError(emailSettings?.notify_on_error                    ?? false);
                setNotifyOnWhitelistChange(emailSettings?.notify_on_whitelist_change ?? false);
                setNotifyOnSuccessNoChanges(emailSettings?.notify_on_success_no_changes ?? false);
            }
        } catch (error) {
            console.log("error loadData: ", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setLoading(true);
            setShowError("");

            const payload = {
                saved_task_name:          values?.saved_task_name,
                function_id:              pathname.split("/").pop(),
                domains:                  values?.domains,
                custom_parameters:        values?.custom_parameters,
                validation_schedule:      values?.validation_schedule,
                variable_name_blacklist:  varBlacklist,
                variable_name_whitelist:  varWhitelist,
                path_crawl_email_settings: {
                    recipient_emails:             pathEmailRecipients,
                    notify_on_error:              notifyOnError,
                    notify_on_whitelist_change:   notifyOnWhitelistChange,
                    notify_on_success_no_changes: notifyOnSuccessNoChanges,
                },
            };

            const result = saved_task_id
                ? await updateTask(userData.token, userData.selectedOrganization.org_token, { id: saved_task_id, data: payload })
                : await saveTask(userData.token, userData.selectedOrganization.org_token, payload);

            toast.success(result.message);

            const taskId = saved_task_id || result?.data?._id || result?.data?.id;

            // Link selected journeys to the task
            if (taskId) {
                try {
                    await linkJourneysToTask(
                        userData.token,
                        userData.selectedOrganization.org_token,
                        taskId,
                        [...selectedJourneyIds]
                    );
                } catch (linkErr) {
                    toast.error("Task saved but journey links could not be saved: " + (linkErr?.message ?? "unknown error"));
                }
            }

            // Run validation
            if (taskId) {
                try {
                    await validateTealiumDataLayer(userData.token, userData.selectedOrganization.org_token, taskId);
                    toast.success("Tealium data layer validation completed.");
                } catch (validationError) {
                    toast.error(validationError?.message || "Validation ran but encountered an error.");
                }
            }

            navigate("/functions/saved-tasks");
        } catch (error) {
            setShowError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const handleOpenVisibilityModal = (customParams) => {
        setVisibilityParams(customParams.map((p) => ({ ...p })));
        setVisibilityModal(true);
    };

    const handleToggleVisibility = (index) =>
        setVisibilityParams((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], visibility: !updated[index].visibility };
            return updated;
        });

    const handleToggleCustomMapping = (index) =>
        setVisibilityParams((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                custom_mapping_enabled: !updated[index].custom_mapping_enabled,
                custom_mapping_key: !updated[index].custom_mapping_enabled
                    ? updated[index].custom_mapping_key || ""
                    : "",
            };
            return updated;
        });

    const handleCustomMappingKeyChange = (index, value) =>
        setVisibilityParams((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], custom_mapping_key: value };
            return updated;
        });

    return (
        <>
            <div className="w-full max-w-screen px-4 md:px-8 my-6">
                {loading && <Loader />}
                {showSuccess && (
                    <SuccessAlert disappearTime={content.CREATE_AT_AUDIENCE_FROM_TEALIUM_CONTENT.timeout}>
                        {content.CREATE_AT_AUDIENCE_FROM_TEALIUM_CONTENT.success}
                    </SuccessAlert>
                )}
                <div className="flex flex-col w-full lg:w-1/2 justify-center items-start">
                    <div className="space-y-4 md:space-y-6 w-full">
                        <Formik
                            enableReinitialize
                            validateOnMount={false}
                            validateOnBlur={true}
                            validateOnChange={false}
                            initialValues={{
                                saved_task_name:     data?.saved_task_name ?? "",
                                domains:             data?.domains ?? "",
                                custom_param_input:  "",
                                custom_parameters:   data?.custom_parameters ?? [],
                                validation_schedule: {
                                    ...(data?.validation_schedule ?? { rules: [] }),
                                    time_interval: data?.validation_schedule?.time_interval ?? "",
                                    status:        data?.validation_schedule?.status        ?? "ENABLED",
                                },
                            }}
                            validationSchema={tealiumDataLayerRepositorySchema({
                                saved_task_name_invalid:  "Task name can only contain letters, numbers, dots, hyphens, and underscores",
                                saved_task_name_required: "Task name is required",
                                domains:                  "Domain is required",
                            })}
                            onSubmit={handleSubmit}
                        >
                            {({ errors, touched, setFieldValue, values, submitCount }) => (
                                <Form>
                                    <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">
                                        Tealium Data Layer Repository
                                    </h2>
                                    <p className="mb-6">Tealium Data Layer Repository</p>

                                    {/* Task Name */}
                                    <div className="flex flex-col md:flex-row gap-9 mb-4">
                                        <div className="w-full">
                                            <div className="relative">
                                                <Field
                                                    type="text" id="saved_task_name" name="saved_task_name"
                                                    className="block px-2.5 pb-2.5 pt-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                                    placeholder=" " disabled={saved_task_id}
                                                />
                                                <Label labelFor="saved_task_name">Task Name</Label>
                                                <Tooltip content="Task Name" class_name="-right-6 top-1/2" />
                                            </div>
                                            {errors.saved_task_name && (touched.saved_task_name || submitCount > 0) && (
                                                <p className="mt-2 text-sm text-red-5">{errors.saved_task_name}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Domain */}
                                    <div className="flex flex-col md:flex-row gap-9 mb-4">
                                        <div className="w-full">
                                            <div className="relative">
                                                <Field
                                                    type="text" id="domains" name="domains"
                                                    className="block px-2.5 pb-2.5 pt-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                                    placeholder=" "
                                                />
                                                <Label labelFor="domains">Domain</Label>
                                                <Tooltip content="Domain" class_name="-right-6 top-1/2" />
                                            </div>
                                            {errors.domains && (touched.domains || submitCount > 0) && (
                                                <p className="mt-2 text-sm text-red-5">{errors.domains}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Crawl Schedule */}
                                    <div className="flex flex-col md:flex-row gap-9 mb-4">
                                        <div className="w-full">
                                            <div className="relative w-full">
                                                <Dropdown
                                                    width={99}
                                                    isWidthInPercentage={true}
                                                    initialValue={content.CRON_TIME_INTERVAL.find(x => x.value == values.validation_schedule.time_interval)}
                                                    items={content.CRON_TIME_INTERVAL}
                                                    callback={(val) => setFieldValue("validation_schedule.time_interval", val.value)}
                                                />
                                                <Label labelFor="time_interval">Crawl Interval</Label>
                                            </div>
                                        </div>
                                        <div className="w-full">
                                            <div className="relative w-full">
                                                <Dropdown
                                                    width={99}
                                                    isWidthInPercentage={true}
                                                    initialValue={content.LAMBDA_STATUS_VALUES.find(x => x.value == values.validation_schedule.status)}
                                                    items={content.LAMBDA_STATUS_VALUES}
                                                    callback={(val) => setFieldValue("validation_schedule.status", val.value)}
                                                />
                                                <Label labelFor="status">Crawl Status</Label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Custom Parameters */}
                                    <div className="flex flex-col md:flex-row gap-9 mb-4">
                                        <div className="w-full">
                                            <div className="relative">
                                                <div className="flex flex-wrap items-center gap-1.5 px-2.5 pb-2.5 pt-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus-within:outline-none focus-within:ring-1 focus-within:ring-primary-2 ring-primary peer min-h-[42px]">
                                                    {values.custom_parameters.map((param, i) => (
                                                        <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${param.visibility !== false ? "bg-primary-2/10 text-primary-2" : "bg-gray-200 text-gray-500 line-through"}`}>
                                                            {param.key}
                                                            <button type="button" className="text-primary-2 hover:text-red-5 ml-0.5"
                                                                onClick={() => { const updated = [...values.custom_parameters]; updated.splice(i, 1); setFieldValue("custom_parameters", updated); }}>
                                                                &times;
                                                            </button>
                                                        </span>
                                                    ))}
                                                    <Field
                                                        type="text" id="custom_param_input" name="custom_param_input"
                                                        className="flex-1 min-w-[120px] outline-none bg-transparent border-none p-0 text-sm"
                                                        placeholder={values.custom_parameters.length === 0 ? " " : "Add parameter..."}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault();
                                                                const val = values.custom_param_input?.trim();
                                                                if (val) {
                                                                    setFieldValue("custom_parameters", [...values.custom_parameters, { key: val, visibility: true }]);
                                                                    setFieldValue("custom_param_input", "");
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <Label labelFor="custom_param_input">Custom Parameters</Label>
                                                <Tooltip content="Custom Parameters" class_name="-right-6 top-1/2" />
                                            </div>
                                            <div className="flex items-center justify-between mt-2">
                                                <p className="text-sm">Press Enter to add parameter</p>
                                                {values.custom_parameters.length > 0 && (
                                                    <button type="button" className="text-sm text-primary hover:underline font-medium"
                                                        onClick={() => handleOpenVisibilityModal(values.custom_parameters)}>
                                                        Manage mapping & visibility
                                                    </button>
                                                )}
                                            </div>
                                            {errors.custom_parameters && (touched.custom_parameters || submitCount > 0) && (
                                                <p className="mt-2 text-sm text-red-5">{errors.custom_parameters}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* ── Tabs ── */}
                                    <div className="mt-8">
                                        <div className="flex border-b border-gray-200 mb-6">
                                            {[
                                                { id: "path_crawls",      label: "E2E Journeys" },
                                                { id: "validation_rules", label: "Site Wide"    },
                                            ].map((tab) => (
                                                <button
                                                    key={tab.id}
                                                    type="button"
                                                    onClick={() => setActiveTab(tab.id)}
                                                    className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                                        activeTab === tab.id
                                                            ? "border-primary text-primary"
                                                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                                    }`}
                                                >
                                                    {tab.label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* ── E2E Journeys tab ── */}
                                        {activeTab === "path_crawls" && (<>
                                            <JourneysSection
                                                orgJourneys={orgJourneys}
                                                setOrgJourneys={setOrgJourneys}
                                                selectedJourneyIds={selectedJourneyIds}
                                                setSelectedJourneyIds={setSelectedJourneyIds}
                                                journeyEdits={journeyEdits}
                                                setJourneyEdits={setJourneyEdits}
                                                journeySaving={journeySaving}
                                                setJourneySaving={setJourneySaving}
                                                creatingJourney={creatingJourney}
                                                setCreatingJourney={setCreatingJourney}
                                                token={userData?.token}
                                                organizationId={userData?.selectedOrganization?.org_token}
                                            />

                                            {/* Variable Lists */}
                                            <div className="mt-8">
                                                <div className="flex items-center gap-3 mb-1">
                                                    <h3 className="text-base font-semibold text-black">Variable Lists</h3>
                                                    <div className="relative">
                                                        <Tooltip
                                                            content="Blacklisted variables are hidden in path crawl comparisons. Whitelisted variables are highlighted even when unchanged."
                                                            class_name="-right-6 top-1/2"
                                                        />
                                                    </div>
                                                </div>
                                                <p className="text-sm text-gray-500 mb-5">
                                                    Configure how variables appear in the path crawl compare view.
                                                </p>

                                                {[
                                                    {
                                                        label: "Blacklist",
                                                        desc: "Variables excluded from path crawl comparisons",
                                                        color: "bg-red-100 text-red-700",
                                                        list: varBlacklist,
                                                        setList: setVarBlacklist,
                                                        input: blackInput,
                                                        setInput: setBlackInput,
                                                    },
                                                    {
                                                        label: "Whitelist",
                                                        desc: "Variables highlighted as notable in comparisons",
                                                        color: "bg-indigo-100 text-indigo-700",
                                                        list: varWhitelist,
                                                        setList: setVarWhitelist,
                                                        input: whiteInput,
                                                        setInput: setWhiteInput,
                                                    },
                                                ].map(({ label, desc, color, list, setList, input, setInput }) => (
                                                    <div key={label} className="mb-5">
                                                        <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
                                                        <p className="text-xs text-gray-400 mb-2">{desc}</p>
                                                        <div className="w-full bg-white rounded-lg border border-gray-200">
                                                            <div className="flex flex-wrap gap-1.5 px-2.5 pt-2 pb-1 max-h-36 overflow-y-auto">
                                                                {list.map((name) => (
                                                                    <span key={name} className={`inline-flex items-center gap-1 text-xs font-mono font-medium px-2.5 py-1 rounded-full ${color}`}>
                                                                        {name}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setList(list.filter((v) => v !== name))}
                                                                            className="hover:opacity-60 ml-0.5"
                                                                        >&times;</button>
                                                                    </span>
                                                                ))}
                                                                {list.length === 0 && <span className="text-xs text-gray-300 italic py-1">None added</span>}
                                                            </div>
                                                            <div className={`px-2.5 py-1.5 ${list.length > 0 ? "border-t border-gray-100" : ""}`}>
                                                                <input
                                                                    type="text"
                                                                    value={input}
                                                                    onChange={(e) => setInput(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") {
                                                                            e.preventDefault();
                                                                            const trimmed = input.trim();
                                                                            if (trimmed && !list.includes(trimmed)) setList([...list, trimmed]);
                                                                            setInput("");
                                                                        }
                                                                    }}
                                                                    placeholder="variable_name (Enter to add)"
                                                                    className="w-full outline-none bg-transparent border-none p-0 text-sm font-mono"
                                                                />
                                                            </div>
                                                        </div>
                                                        <p className="text-xs text-gray-400 mt-1">Press Enter to add a variable name</p>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Path Crawl Notifications */}
                                            <div className="mt-8">
                                                <div className="flex items-center gap-3 mb-1">
                                                    <h3 className="text-base font-semibold text-black">Path Crawl Notifications</h3>
                                                    <div className="relative">
                                                        <Tooltip
                                                            content="Send email reports to the listed addresses after a path crawl run."
                                                            class_name="-right-6 top-1/2"
                                                        />
                                                    </div>
                                                </div>
                                                <p className="text-sm text-gray-500 mb-5">
                                                    Configure when email reports are sent after a path crawl completes.
                                                </p>

                                                <div className="mb-5">
                                                    <label className="block text-xs font-semibold text-gray-700 mb-1">Recipient Emails</label>
                                                    <div className="w-full bg-white rounded-lg border border-gray-200">
                                                        <div className="flex flex-wrap gap-1.5 px-2.5 pt-2 pb-1 max-h-36 overflow-y-auto">
                                                            {pathEmailRecipients.map((email) => (
                                                                <span key={email} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-primary-2/10 text-primary-2">
                                                                    {email}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setPathEmailRecipients(pathEmailRecipients.filter((e) => e !== email))}
                                                                        className="hover:opacity-60 ml-0.5"
                                                                    >&times;</button>
                                                                </span>
                                                            ))}
                                                            {pathEmailRecipients.length === 0 && (
                                                                <span className="text-xs text-gray-300 italic py-1">None added</span>
                                                            )}
                                                        </div>
                                                        <div className={`px-2.5 py-1.5 ${pathEmailRecipients.length > 0 ? "border-t border-gray-100" : ""}`}>
                                                            <input
                                                                type="email"
                                                                value={pathEmailInput}
                                                                onChange={(e) => setPathEmailInput(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        const trimmed = pathEmailInput.trim();
                                                                        if (trimmed && !pathEmailRecipients.includes(trimmed)) {
                                                                            setPathEmailRecipients([...pathEmailRecipients, trimmed]);
                                                                        }
                                                                        setPathEmailInput("");
                                                                    }
                                                                }}
                                                                placeholder="email@example.com (Enter to add)"
                                                                className="w-full outline-none bg-transparent border-none p-0 text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-1">Press Enter to add an email address</p>
                                                </div>

                                                <div className="space-y-3">
                                                    {[
                                                        { label: "An error occurred when running the path",   state: notifyOnError,            set: setNotifyOnError },
                                                        { label: "A whitelisted variable was changed",        state: notifyOnWhitelistChange,  set: setNotifyOnWhitelistChange },
                                                        { label: "The path ran successfully with no changes", state: notifyOnSuccessNoChanges, set: setNotifyOnSuccessNoChanges },
                                                    ].map(({ label, state, set }) => (
                                                        <label key={label} className="flex items-center gap-3 cursor-pointer select-none">
                                                            <input
                                                                type="checkbox"
                                                                checked={state}
                                                                onChange={(e) => set(e.target.checked)}
                                                                className="w-4 h-4 accent-primary-2"
                                                            />
                                                            <span className="text-sm text-gray-700">{label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </>)}

                                        {/* ── Validation Rules tab ── */}
                                        {activeTab === "validation_rules" && (
                                            <ValidationRulesSection values={values} setFieldValue={setFieldValue} />
                                        )}
                                    </div>

                                    {showError && <p className="my-2 text-sm text-red-5">{showError}</p>}

                                    <div className="flex justify-end mt-8">
                                        <PrimaryButton type="submit">
                                            {content.CREATE_AT_AUDIENCE_FROM_TEALIUM_CONTENT.submit_cta}
                                        </PrimaryButton>
                                    </div>

                                    {/* Visibility Management Modal */}
                                    {visibilityModal && (
                                        <PrimaryModal title="Manage Field Visibility" hideModal={() => setVisibilityModal(false)} widthClass="max-w-lg">
                                            <div className="p-4 max-h-[70vh] overflow-y-auto">
                                                {visibilityParams.length === 0 ? (
                                                    <p className="text-gray-500 italic text-center py-4">No custom parameters available.</p>
                                                ) : (
                                                    <div className="space-y-3">
                                                        <p className="text-sm text-gray-500 mb-4">
                                                            Toggle visibility of custom parameters. Only visible fields will appear in the report details view.
                                                        </p>
                                                        {visibilityParams.map((param, index) => (
                                                            <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm font-medium text-gray-900 capitalize">{param.key?.replace(/_/g, " ")}</span>
                                                                    <Toggle
                                                                        id={`param-visibility-${index}`}
                                                                        checked={param.visibility !== false}
                                                                        onChange={() => handleToggleVisibility(index)}
                                                                        size="sm"
                                                                    />
                                                                </div>
                                                                <div className="mt-2 flex items-center gap-2">
                                                                    <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-600">
                                                                        <input type="checkbox" checked={param.custom_mapping_enabled || false} onChange={() => handleToggleCustomMapping(index)} className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer" />
                                                                        Custom Mapping
                                                                    </label>
                                                                </div>
                                                                {param.custom_mapping_enabled && (
                                                                    <div className="mt-2">
                                                                        <input
                                                                            type="text" value={param.custom_mapping_key || ""}
                                                                            onChange={(e) => handleCustomMappingKeyChange(index, e.target.value)}
                                                                            placeholder="Enter utag key to map from"
                                                                            className="w-full p-2 text-sm text-gray-900 bg-white rounded border border-gray-300 focus:outline-none focus:border-primary"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {visibilityParams.length > 0 && (
                                                    <div className="flex justify-end mt-4">
                                                        <button type="button"
                                                            onClick={() => { setFieldValue("custom_parameters", visibilityParams); setVisibilityModal(false); }}
                                                            className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90">
                                                            Apply
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </PrimaryModal>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TealiumDataLayerRepository;
